import type { Theme } from "@mui/material/styles";
import { gridClasses } from "@mui/x-data-grid";

export const MuiDataGrid = {
  styleOverrides: {
    root: ({ theme }: { theme: Theme }) => {
      const palette = (theme.vars || theme).palette;

      return {
        "--bg-selected-default": palette.action.selected,
        "--bg-hover-default": palette.action.hover,
        "--bg-hover-darkness-level": "50%",
        "--bg-selected-darkness-level": "10%",
        "--mui-palette-DataGrid-headerBg": "var(--mui-palette-background-paper)",
        "--mui-palette-DataGrid-bg": "var(--mui-palette-background-paper)",

        containerType: "inline-size",
        fontSize: 12,
        border: "none",
        width: "100%",

        "@container (min-width: 1100px)": {
          ".MuiDataGrid-main": {
            fontSize: 14,
          },
        },

        "& .MuiDataGrid-filler": {
          pointerEvents: "none",
        },

        "& .MuiDataGrid-virtualScrollerRenderZone": {
          "::-webkit-scrollbar": {
            background: "aquamarine",
          },
          [`.${gridClasses.row}`]: {
            // backdropFilter: "brightness(var(--bg-brightness))",
            backgroundColor: `var(--bg-color)`,
            mixBlendMode: "multiply",

            "&[aria-level]:not([aria-level='1'])": {
              transition: `${theme.transitions.duration.shortest}ms ${theme.transitions.easing.easeOut}`,
              willChange: "opacity, transform",

              "@starting-style": {
                opacity: 0,
                transform: "translateY(-50%)",
              },
            },

            [`.${gridClasses.cell}`]: {
              // applying "mixBlendMode" allows hover background color change to apply also on cells which have they own background color set
              mixBlendMode: "normal",
              ...theme.applyStyles("dark", { mixBlendMode: "screen" }),

              // Style cell elements where the class name contains 'bg-'
              "&[class*='bg-']": {
                mixBlendMode: "inherit",
                ...theme.applyStyles("dark", { mixBlendMode: "screen" }),
              },
            },

            [`.${gridClasses["cell--pinnedRight"]}`]: {
              mixBlendMode: "normal",
              // A trick so other columns won't "bleed" into the pinned column while the pinned columns maintains the correct
              background: "var(--mui-palette-background-paper)",
              "::after": {
                content: "''",
                position: "absolute",
                inset: 0,
                background: "var(--bg-selected, var(--bg-color))",
                pointerEvents: "none",
              },
              "> *": {
                position: "relative",
                zIndex: 1,
              },
              // ...theme.applyStyles("dark", { mixBlendMode: "screen" }),
            },

            "&.Mui-selected": {
              "--_bg-selected": `color-mix(in srgb, var(--bg-color), black var(--bg-selected-darkness-level));`,
              "--bg-selected": `var(--_bg-selected, var(--bg-selected-default))`,
              backgroundColor: `var(--bg-selected)`,

              // if row has no "--bg-color" variable set, use the "--bg-selected-default" and NOT "--bg-hover-default" as the background color
              // because selected rows should not have a hover background color-change by default
              "&:hover": {
                "--bg-selected": `color-mix(in srgb, var(--bg-color), black var(--bg-selected-darkness-level));`,
                backgroundColor: `var(--bg-selected, var(--bg-selected-default))`,
              },

              [`.${gridClasses["cell--pinnedRight"]}`]: {
                background: "var(--mui-palette-background-paper)",
              },
            },

            // hover states (2 identical states)
            "&:hover": {
              "--bg-hover": `color-mix(in srgb, var(--bg-color), var(--mui-palette-action-hover) var(--bg-hover-darkness-level))`,
              backgroundColor: `var(--bg-hover, var(--bg-hover-default))`,
            },
          },

          [`.${gridClasses["row--firstVisible"]}`]: {
            [`.${gridClasses.cell}`]: {
              borderTop: "none",
            },
          },
        },

        // Conditional hover styles when with-row-hover class is present
        "&.with-row-hover": {
          [`& .${gridClasses.row}`]: {
            "&:hover": {
              "--bg-color": "var(--bg-hover-default)",
            },
          },
        },
      };
    },
    cell: {
      "&:focus, &:focus-within": {
        outline: "none",
      },
    },
    columnHeader: {
      "&:focus, &:focus-within": {
        outline: "none",
      },
    },
    pinnedColumns: {
      [`.${gridClasses.row}`]: {},
    },
  },
};
